/**
 * Routes (PRD §9). Every page is a lazy chunk → the first load only ships the shell + Home.
 * `handle` flags hide the search bar / bottom nav per PRD §4 navigation notes.
 */
import { createBrowserRouter, type RouteObject } from 'react-router'

import { AppShell } from '@/components/layout/AppShell'
import { loadPage } from '@/lib/staleChunk'
import ErrorPage from '@/pages/ErrorPage'

import type { RouteHandle } from './routeHandle'

type PageModule = { default: React.ComponentType }
const page = (load: () => Promise<PageModule>) => async () => ({
  Component: (await loadPage(load)).default,
})

export const routes: RouteObject[] = [
  {
    element: <AppShell />,
    errorElement: <ErrorPage />,
    // Lazy pages load after the first paint; nothing to show meanwhile (silences the dev warning).
    hydrateFallbackElement: null,
    children: [
      { index: true, lazy: page(() => import('@/pages/HomePage')) },
      { path: 'shops', lazy: page(() => import('@/pages/ShopsPage')) },
      { path: 'category/:slug', lazy: page(() => import('@/pages/CategoryPage')) },
      { path: 'collections/:slug', lazy: page(() => import('@/pages/CollectionPage')) },
      { path: 'section/:code', lazy: page(() => import('@/pages/SectionPage')) },
      {
        path: 'shop/:slug',
        lazy: page(() => import('@/pages/ShopPage')),
        handle: { hideSearchBar: true } satisfies RouteHandle,
      },
      {
        path: 'product/:idSlug',
        lazy: page(() => import('@/pages/ProductPage')),
        handle: { hideSearchBar: true, hideBottomNav: true } satisfies RouteHandle,
      },
      { path: 'search', lazy: page(() => import('@/pages/SearchPage')) },
      { path: 'cart', lazy: page(() => import('@/pages/CartPage')) },
      { path: 'privacy', lazy: page(() => import('@/pages/PrivacyPage')) },
      { path: '*', lazy: page(() => import('@/pages/NotFoundPage')) },
    ],
  },
]

export const router = createBrowserRouter(routes)
